import { Component, CUSTOM_ELEMENTS_SCHEMA, inject, signal, computed, Injector } from '@angular/core';
import { ModalController, IonicModule } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NgComponentOutlet } from '@angular/common';
import { AppState } from 'src/app/app.module';
import { settingsFeature } from 'src/app/ngrx/settings/settings.feature';
import { WizardService } from './wizard.service';
import { LOGGER } from 'src/app/oidc/shared/logger';
import { stepsFor, WizardStep } from './wizard-catalogue';
import { WIZARD_STEP_HOST } from './wizard-step-host';

@Component({
  selector: 'app-wizard',
  templateUrl: './wizard.page.html',
  styles: `
    ion-title {
      position: static;
      padding-inline: 16px;
      text-align: center;
    }

    .navigation {
      display: grid;
    }

    .navigation.with-back {
      grid-template-columns: minmax(0, 1fr) minmax(0, 1.5fr);
      gap: 12px;
    }

    .navigation ion-button {
      margin: 0;
      white-space: nowrap;
    }
  `,
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [IonicModule, TranslatePipe, NgComponentOutlet],
})
export class WizardPage {
  private readonly store = inject<Store<AppState>>(Store);
  public readonly translate = inject(TranslateService);

  /**
   * What makes `WIZARD_STEP_HOST` reachable from inside a step rendered through
   * `NgComponentOutlet`, which otherwise gets this component's injector without it.
   */
  readonly stepInjector = Injector.create({
    parent: inject(Injector),
    providers: [{ provide: WIZARD_STEP_HOST, useValue: this }],
  });

  private readonly wizard = inject(WizardService);
  private readonly modalCtrl = inject(ModalController);
  private readonly log = inject(LOGGER).scope('wizard');

  /**
   * A snapshot, taken once when the modal opens. Reading the queue reactively would pull
   * the current step out from under the user the moment it is completed — the store is
   * where a step is finished, the local index is what the user is walking along.
   */
  readonly queue = stepsFor(this.store.selectSignal(settingsFeature.selectWizardPending)());
  readonly index = signal(0);

  /**
   * Explicitly optional: `noUncheckedIndexedAccess` is off, so the index access alone
   * would claim a step is always there and hide the empty-queue case from every caller.
   */
  readonly current = computed<WizardStep | undefined>(() => this.queue[this.index()]);
  readonly isLast = computed(() => this.index() >= this.queue.length - 1);

  goBack(): void {
    this.index.update(index => Math.max(0, index - 1));
  }

  advance(): void {
    const step = this.current();
    if (!step) {
      void this.close();
      return;
    }
    const last = this.isLast();
    this.wizard.complete(step.id);
    if (last) void this.close();
    else this.index.update(index => index + 1);
  }

  async handOver(): Promise<void> {
    const step = this.current();
    if (step) this.wizard.complete(step.id);
    await this.close();
  }

  private async close(): Promise<void> {
    await this.modalCtrl.dismiss().catch((e: unknown) =>
      this.log.warn('closing the wizard failed', { error: String(e) }),
    );
  }
}
